import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const ORCH_API = '/orchestrator-api/api';

const S = {
  page: { padding: 32, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0' },
  subtitle: { fontSize: 13, color: '#8888aa', marginTop: 4 },
  card: {
    background: '#1a1a2e', borderRadius: 12, border: '1px solid #2a2a4a',
    padding: 20, marginBottom: 16,
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2a2a4a',
    color: '#8888aa', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a1a2a', color: '#e2e8f0' },
  btn: (variant) => ({
    padding: '8px 16px', borderRadius: 8, border: '1px solid',
    cursor: 'pointer', fontSize: 12, fontWeight: 600,
    ...(variant === 'primary' ? { background: '#3b82f620', color: '#3b82f6', borderColor: '#3b82f650' } :
      variant === 'danger' ? { background: '#ef444420', color: '#ef4444', borderColor: '#ef444450' } :
      variant === 'success' ? { background: '#22c55e20', color: '#22c55e', borderColor: '#22c55e50' } :
      { background: '#16213e', color: '#e2e8f0', borderColor: '#2a2a4a' }),
  }),
  badge: (color) => ({
    fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 600,
    background: `${color}20`, color: color,
  }),
  stat: { fontSize: 12, color: '#8888aa' },
  uploadZone: {
    border: '2px dashed #2a2a4a', borderRadius: 12, padding: 32,
    textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s',
    background: '#0d1117',
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 12 },
};

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function formatDate(d) {
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AiKnowledgeBase() {
  const { apiFetch } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [engagements, setEngagements] = useState([]);
  const [selectedEngagement, setSelectedEngagement] = useState('');
  const [filterScope, setFilterScope] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState('');
  const [newRuleCategory, setNewRuleCategory] = useState('general');
  const [editingRule, setEditingRule] = useState(null); // { id, rule_text, category }
  const [editingFile, setEditingFile] = useState(null); // { id, filename, content, saving }

  const token = sessionStorage.getItem('token');

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${ORCH_API}/ai/knowledge`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setFiles(d.files || []);
      }
    } catch {}
    setLoading(false);
  }, [token]);

  const loadEngagements = useCallback(async () => {
    try {
      const r = await apiFetch('/engagements?limit=100');
      if (r.ok) {
        const d = await r.json();
        setEngagements(d.engagements || d || []);
      }
    } catch {}
  }, [apiFetch]);

  const loadRules = useCallback(async () => {
    try {
      const r = await fetch(`${ORCH_API}/ai/rules`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) { const d = await r.json(); setRules(d.rules || []); }
    } catch {}
  }, [token]);

  const addRule = useCallback(async () => {
    if (!newRule.trim()) return;
    await fetch(`${ORCH_API}/ai/rules`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ rule_text: newRule.trim(), category: newRuleCategory }),
    });
    setNewRule('');
    loadRules();
  }, [token, newRule, newRuleCategory, loadRules]);

  const toggleRule = useCallback(async (rule) => {
    await fetch(`${ORCH_API}/ai/rules/${rule.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...rule, is_enabled: !rule.is_enabled }),
    });
    loadRules();
  }, [token, loadRules]);

  const deleteRule = useCallback(async (id) => {
    await fetch(`${ORCH_API}/ai/rules/${id}`, {
      method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
    });
    loadRules();
  }, [token, loadRules]);

  const saveEditRule = useCallback(async () => {
    if (!editingRule || !editingRule.rule_text.trim()) return;
    await fetch(`${ORCH_API}/ai/rules/${editingRule.id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(editingRule),
    });
    setEditingRule(null);
    loadRules();
  }, [token, editingRule, loadRules]);

  useEffect(() => { loadFiles(); loadEngagements(); loadRules(); }, [loadFiles, loadEngagements, loadRules]);

  // Poll for processing files
  useEffect(() => {
    const processing = files.filter(f => f.status === 'processing');
    if (processing.length === 0) return;
    const interval = setInterval(loadFiles, 3000);
    return () => clearInterval(interval);
  }, [files, loadFiles]);

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);

    for (const file of fileList) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('engagement_id', selectedEngagement || '');

      try {
        await fetch(`${ORCH_API}/ai/knowledge/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
      } catch {}
    }

    setUploading(false);
    loadFiles();
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this knowledge file and all its chunks?')) return;
    await fetch(`${ORCH_API}/ai/knowledge/${fileId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    loadFiles();
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Delete ALL knowledge base files? This cannot be undone.')) return;
    for (const f of files) {
      await fetch(`${ORCH_API}/ai/knowledge/${f.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    }
    loadFiles();
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const params = new URLSearchParams({ q: searchQuery, limit: '10' });
      const r = await fetch(`${ORCH_API}/ai/knowledge/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setSearchResults(d);
      }
    } catch {}
    setSearching(false);
  };

  const filteredFiles = files.filter(f => {
    if (filterScope === 'global') return f.is_global;
    if (filterScope === 'engagement') return !f.is_global;
    return true;
  });

  const totalChunks = files.reduce((sum, f) => sum + (f.chunk_count || 0), 0);
  const totalSize = files.reduce((sum, f) => sum + (f.file_size_bytes || 0), 0);
  const readyFiles = files.filter(f => f.status === 'ready').length;
  const processingFiles = files.filter(f => f.status === 'processing').length;

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>AI Knowledge Base</div>
          <div style={S.subtitle}>
            Upload pentest knowledge files to enhance the AI Copilot with your payloads, cheatsheets, and methodology docs.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {files.length > 0 && (
            <button style={S.btn('danger')} onClick={handleDeleteAll}>Delete All</button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Files', value: files.length, color: '#3b82f6' },
          { label: 'Ready', value: readyFiles, color: '#22c55e' },
          { label: 'Processing', value: processingFiles, color: '#f59e0b' },
          { label: 'Chunks', value: totalChunks, color: '#a855f7' },
          { label: 'Total Size', value: formatBytes(totalSize), color: '#8888aa' },
        ].map(s => (
          <div key={s.label} style={{
            ...S.card, flex: 1, padding: '12px 16px', textAlign: 'center', marginBottom: 0,
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#8888aa', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* AI Rules Section */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={S.sectionTitle}>AI Behavioral Rules</div>
          <span style={{ fontSize: 11, color: '#8888aa' }}>
            {rules.filter(r => r.is_enabled).length} active / {rules.length} total
          </span>
        </div>
        <div style={{ fontSize: 12, color: '#8888aa', marginBottom: 12, lineHeight: 1.5 }}>
          Rules are injected into every AI prompt. The AI MUST follow these rules. Use them to control behavior,
          restrict actions, or enforce methodology.
        </div>

        {/* Add new rule */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <select
            value={newRuleCategory}
            onChange={e => setNewRuleCategory(e.target.value)}
            style={{
              padding: '8px 10px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 8, fontSize: 11, width: 120,
            }}
          >
            <option value="safety">Safety</option>
            <option value="methodology">Methodology</option>
            <option value="scope">Scope</option>
            <option value="reporting">Reporting</option>
            <option value="general">General</option>
          </select>
          <input
            type="text"
            value={newRule}
            onChange={e => setNewRule(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addRule()}
            placeholder="Enter a rule (e.g., 'Never use destructive HTTP methods like DELETE or PUT on production targets')"
            style={{
              flex: 1, padding: '8px 12px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 8, fontSize: 12, outline: 'none',
            }}
          />
          <button style={S.btn('success')} onClick={addRule} disabled={!newRule.trim()}>Add Rule</button>
        </div>

        {/* Rules list */}
        {rules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#8888aa', fontSize: 12 }}>
            No rules defined. Add rules to control AI behavior during testing.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rules.map(rule => {
              const isEditing = editingRule?.id === rule.id;
              return (
                <div key={rule.id} style={{
                  display: 'flex', alignItems: isEditing ? 'flex-start' : 'center', gap: 8, padding: '8px 12px',
                  background: isEditing ? '#1a1a3e' : rule.is_enabled ? '#16213e' : '#0d1117',
                  borderRadius: 8, border: `1px solid ${isEditing ? '#3b82f650' : rule.is_enabled ? '#2a2a4a' : '#1a1a2a'}`,
                  opacity: rule.is_enabled ? 1 : 0.5,
                }}>
                  <button
                    onClick={() => toggleRule(rule)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0,
                      color: rule.is_enabled ? '#22c55e' : '#8888aa', marginTop: isEditing ? 4 : 0,
                    }}
                    title={rule.is_enabled ? 'Disable' : 'Enable'}
                  >{rule.is_enabled ? '\u2705' : '\u2B1C'}</button>

                  {isEditing ? (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select
                          value={editingRule.category}
                          onChange={e => setEditingRule({ ...editingRule, category: e.target.value })}
                          style={{
                            padding: '4px 8px', background: '#16213e', color: '#e2e8f0',
                            border: '1px solid #2a2a4a', borderRadius: 4, fontSize: 11,
                          }}
                        >
                          <option value="safety">Safety</option>
                          <option value="methodology">Methodology</option>
                          <option value="scope">Scope</option>
                          <option value="reporting">Reporting</option>
                          <option value="general">General</option>
                        </select>
                      </div>
                      <input
                        type="text"
                        value={editingRule.rule_text}
                        onChange={e => setEditingRule({ ...editingRule, rule_text: e.target.value })}
                        onKeyDown={e => { if (e.key === 'Enter') saveEditRule(); if (e.key === 'Escape') setEditingRule(null); }}
                        autoFocus
                        style={{
                          padding: '6px 10px', background: '#0d1117', color: '#e2e8f0',
                          border: '1px solid #3b82f650', borderRadius: 6, fontSize: 12, outline: 'none', width: '100%',
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...S.btn('primary'), padding: '3px 10px', fontSize: 10 }} onClick={saveEditRule}>Save</button>
                        <button style={{ ...S.btn(), padding: '3px 10px', fontSize: 10 }} onClick={() => setEditingRule(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <span style={{
                        ...S.badge(
                          rule.category === 'safety' ? '#ef4444' :
                          rule.category === 'methodology' ? '#3b82f6' :
                          rule.category === 'scope' ? '#f59e0b' :
                          rule.category === 'reporting' ? '#a855f7' : '#8888aa'
                        ),
                        flexShrink: 0,
                      }}>{rule.category}</span>
                      <span
                        style={{ flex: 1, fontSize: 12, color: '#e2e8f0', cursor: 'pointer' }}
                        onClick={() => setEditingRule({ id: rule.id, rule_text: rule.rule_text, category: rule.category, is_enabled: rule.is_enabled, priority: rule.priority })}
                        title="Click to edit"
                      >{rule.rule_text}</span>
                      <button
                        onClick={() => setEditingRule({ id: rule.id, rule_text: rule.rule_text, category: rule.category, is_enabled: rule.is_enabled, priority: rule.priority })}
                        style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 11, padding: '0 4px' }}
                        title="Edit rule"
                      >&#x270F;</button>
                      <button
                        onClick={() => deleteRule(rule.id)}
                        style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}
                        title="Delete rule"
                      >&times;</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div style={S.card}>
        <div style={S.sectionTitle}>Upload Knowledge Files</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
          <select
            value={selectedEngagement}
            onChange={e => setSelectedEngagement(e.target.value)}
            style={{
              padding: '8px 12px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 8, fontSize: 12,
            }}
          >
            <option value="">Global (all engagements)</option>
            {engagements.map(e => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: '#8888aa' }}>
            {selectedEngagement ? 'File will be scoped to selected engagement' : 'File will be available across all engagements'}
          </span>
        </div>

        <div
          style={{
            ...S.uploadZone,
            borderColor: uploading ? '#f59e0b' : '#2a2a4a',
          }}
          onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3b82f6'; }}
          onDragLeave={e => { e.currentTarget.style.borderColor = '#2a2a4a'; }}
          onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#2a2a4a'; handleUpload(e.dataTransfer.files); }}
          onClick={() => document.getElementById('kb-file-input').click()}
        >
          <input
            id="kb-file-input"
            type="file"
            multiple
            accept=".txt,.md,.json,.csv"
            style={{ display: 'none' }}
            onChange={e => { handleUpload(e.target.files); e.target.value = ''; }}
            disabled={uploading}
          />
          <div style={{ fontSize: 32, marginBottom: 8 }}>{uploading ? '...' : '+'}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 4 }}>
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </div>
          <div style={{ fontSize: 12, color: '#8888aa' }}>
            Accepted: .txt, .md, .json, .csv (max 10MB each)
          </div>
          <div style={{ fontSize: 11, color: '#8888aa', marginTop: 4 }}>
            XSS payloads, SQLi cheatsheets, methodology docs, OWASP guides, custom wordlists...
          </div>
        </div>
      </div>

      {/* Search Test */}
      <div style={S.card}>
        <div style={S.sectionTitle}>Search Knowledge Base</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Test a search query (e.g., 'XSS payloads for attribute context')..."
            style={{
              flex: 1, padding: '8px 12px', background: '#16213e', color: '#e2e8f0',
              border: '1px solid #2a2a4a', borderRadius: 8, fontSize: 12, outline: 'none',
            }}
          />
          <button style={S.btn('primary')} onClick={handleSearch} disabled={searching}>
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>
        {searchResults && (
          <div>
            <div style={{ fontSize: 11, color: '#8888aa', marginBottom: 8 }}>
              {searchResults.count} result{searchResults.count !== 1 ? 's' : ''} for "{searchResults.query}"
            </div>
            {(searchResults.results || []).map((r, i) => (
              <div key={i} style={{
                background: '#0d1117', borderRadius: 8, padding: 12, marginBottom: 8,
                border: '1px solid #2a2a4a',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#3b82f6' }}>{r.filename}</span>
                  <span style={{ fontSize: 10, color: '#22c55e' }}>similarity: {r.similarity}</span>
                </div>
                <pre style={{
                  fontSize: 11, color: '#b0b0cc', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  margin: 0, maxHeight: 150, overflow: 'auto', lineHeight: 1.5,
                  fontFamily: '"JetBrains Mono", "Cascadia Code", monospace',
                }}>{r.content}</pre>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File List */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={S.sectionTitle}>Uploaded Files</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['all', 'global', 'engagement'].map(scope => (
              <button
                key={scope}
                style={{
                  ...S.btn(filterScope === scope ? 'primary' : undefined),
                  padding: '4px 10px', fontSize: 11,
                }}
                onClick={() => setFilterScope(scope)}
              >
                {scope === 'all' ? 'All' : scope === 'global' ? 'Global' : 'Engagement'}
              </button>
            ))}
            <button style={S.btn()} onClick={loadFiles}>Refresh</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8888aa' }}>Loading...</div>
        ) : filteredFiles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8888aa' }}>
            No knowledge files {filterScope !== 'all' ? `(${filterScope})` : ''}. Upload some above!
          </div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Filename</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>Size</th>
                <th style={S.th}>Chunks</th>
                <th style={S.th}>Scope</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Uploaded</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFiles.map(f => (
                <tr key={f.id} style={{ borderBottom: '1px solid #1a1a2a' }}>
                  <td style={{ ...S.td, fontWeight: 600, fontFamily: 'monospace', fontSize: 12 }}>{f.filename}</td>
                  <td style={S.td}>
                    <span style={S.badge('#8888aa')}>{f.file_type}</span>
                  </td>
                  <td style={S.td}>{formatBytes(f.file_size_bytes)}</td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 700, color: f.chunk_count > 0 ? '#a855f7' : '#8888aa' }}>
                      {f.chunk_count}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(f.is_global ? '#3b82f6' : '#f59e0b')}>
                      {f.is_global ? 'Global' : 'Engagement'}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={S.badge(
                      f.status === 'ready' ? '#22c55e' : f.status === 'error' ? '#ef4444' : '#f59e0b'
                    )}>
                      {f.status}
                    </span>
                    {f.error_message && (
                      <div style={{ fontSize: 10, color: '#ef4444', marginTop: 2 }}>{f.error_message}</div>
                    )}
                  </td>
                  <td style={{ ...S.td, fontSize: 11 }}>{formatDate(f.created_at)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button
                      style={{ ...S.btn(), padding: '4px 10px', fontSize: 11, marginRight: 4 }}
                      onClick={async () => {
                        try {
                          const res = await fetch(`${ORCH_API}/ai/knowledge/${f.id}/content`, {
                            headers: { 'Authorization': `Bearer ${token}` },
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setEditingFile({ id: f.id, filename: data.filename, content: data.content, saving: false });
                          }
                        } catch (e) { console.error('Failed to load file content:', e); }
                      }}
                    >Edit</button>
                    <button
                      style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: 11 }}
                      onClick={() => handleDelete(f.id)}
                    >Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* File Editor Modal */}
      {editingFile && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999 }}
            onClick={() => !editingFile.saving && setEditingFile(null)} />
          <div style={{
            position: 'fixed', top: '5%', left: '10%', right: '10%', bottom: '5%',
            background: '#0d1117', border: '1px solid #2a2a4a', borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,.5)', display: 'flex', flexDirection: 'column',
            zIndex: 10000,
          }}>
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #2a2a4a', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 16 }}>&#x1F4DD;</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0' }}>Edit: {editingFile.filename}</div>
                <div style={{ fontSize: 10, color: '#8888aa' }}>Changes will re-chunk and re-embed the file</div>
              </div>
              <button onClick={() => !editingFile.saving && setEditingFile(null)}
                style={{ background: 'none', border: 'none', color: '#8888aa', fontSize: 16, cursor: 'pointer' }}>&times;</button>
            </div>
            <textarea
              value={editingFile.content}
              onChange={e => setEditingFile(prev => ({ ...prev, content: e.target.value }))}
              disabled={editingFile.saving}
              style={{
                flex: 1, margin: '12px 20px', padding: 12, background: '#16213e', color: '#e2e8f0',
                border: '1px solid #2a2a4a', borderRadius: 8, fontFamily: 'monospace', fontSize: 12,
                resize: 'none', outline: 'none', lineHeight: 1.5,
              }}
            />
            <div style={{ padding: '12px 20px', borderTop: '1px solid #2a2a4a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#8888aa' }}>
                {editingFile.content.length.toLocaleString()} chars, ~{editingFile.content.split('\n').length} lines
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.btn()} onClick={() => setEditingFile(null)} disabled={editingFile.saving}>Cancel</button>
                <button
                  style={{ ...S.btn('primary'), background: '#a855f7' }}
                  disabled={editingFile.saving}
                  onClick={async () => {
                    setEditingFile(prev => ({ ...prev, saving: true }));
                    try {
                      const res = await fetch(`${ORCH_API}/ai/knowledge/${editingFile.id}/content`, {
                        method: 'PUT',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ content: editingFile.content }),
                      });
                      if (res.ok) {
                        setEditingFile(null);
                        loadFiles();
                      } else {
                        const err = await res.text();
                        alert('Save failed: ' + err);
                        setEditingFile(prev => ({ ...prev, saving: false }));
                      }
                    } catch (e) {
                      alert('Save failed: ' + e.message);
                      setEditingFile(prev => ({ ...prev, saving: false }));
                    }
                  }}
                >{editingFile.saving ? 'Saving & Re-processing...' : 'Save & Re-process'}</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
